/* eslint-disable prefer-arrow-callback */
import React, { useEffect } from "react";

import { getCurrencySymbol, productAnalytics, useLocation, useNavigate, useTranslation, useUserContext } from "utils";

import { INPUT_COST_PRESET_PCT, OUTPUT_COST_PRESET_PCT, PDF_PAGES_TOKEN, PDF_PAGE_TOKEN } from "consts";

import { MainPage, PageSpin, Svg, Typography } from "components";

import { EconomicsSection } from "pages/app/scenario-analysis/components/EconomicsSection";
import { KeyInsightCards } from "pages/app/scenario-analysis/components/KeyInsightCards";
import { SectionHeading } from "pages/app/scenario-analysis/components/SectionHeading";
import { SideBySideSection } from "pages/app/scenario-analysis/components/SideBySideSection";
import { NumberedSectionHeader } from "pages/app/scenario-analysis/components/pdf-report/NumberedSectionHeader";
import { PdfChartAndTable } from "pages/app/scenario-analysis/components/pdf-report/PdfChartAndTable";
import { ScenarioLegendItem } from "pages/app/scenario-analysis/components/pdf-report/ScenarioLegendItem";
import { ScenarioPdfCard } from "pages/app/scenario-analysis/components/pdf-report/ScenarioPdfCard";
import { useInjectedStylesheet } from "pages/app/scenario-analysis/hooks/useInjectedStylesheet";
import { usePdfReportData } from "pages/app/scenario-analysis/hooks/usePdfReportData";
import { usePrintWhenReady } from "pages/app/scenario-analysis/hooks/usePrintWhenReady";
import { ViewMode } from "pages/app/scenario-analysis/utils/forecast";
import { presetPctLabel } from "pages/app/scenario-analysis/utils/forecastPresets";
import { buildPdfPageStyle } from "pages/app/scenario-analysis/utils/pdfPageStyle";
import { ScenarioPrintState } from "pages/app/scenario-analysis/utils/printState";

const noop = () => undefined;

export const ScenarioPdfReport = () => {
  const { t } = useTranslation(["scenarioAnalysis", "meta"]);
  const { user } = useUserContext();
  const navigate = useNavigate();
  const { state: locationState } = useLocation() as { state: ScenarioPrintState };

  const {
    scenarios,
    soilSightOutcomes,
    resilienceScores,
    settings,
    currency,
    projectionYears,
    mainCropLabelLookup,
    isPracticeCropsFetched,
    forecastData,
    baseline,
    keyInsightRows,
  } = usePdfReportData(locationState);

  useInjectedStylesheet(buildPdfPageStyle(t("pdfReport.footerBrand"), t("pdfReport.footerPage", { page: PDF_PAGE_TOKEN, pages: PDF_PAGES_TOKEN })));

  // Nothing to print without location state (direct hit or refresh), so send them back.
  useEffect(
    function redirectWithoutScenarios() {
      if (!scenarios.length) {
        navigate("/scenarios", { replace: true });
      }
    },
    [scenarios.length, navigate]
  );

  // Waits for crop names, so rotations don't print "—" while the query is in flight; skipped while the guard above redirects.
  const { printReady } = usePrintWhenReady({
    hasContent: scenarios.length > 0,
    isDataReady: isPracticeCropsFetched,
    onBeforePrint: () => productAnalytics.track("soilsights:report_print", { scenario_count: scenarios.length }),
    onAfterPrint: () => navigate(-1),
  });

  if (!scenarios.length) {
    return null;
  }

  const settingPills = [
    { key: "timeline", label: t("forecastSettingsBar.timeline"), value: t("forecastSettingsBar.timelineOption", { years: projectionYears }) },
    { key: "market", label: t("forecastSettingsBar.outputCosts"), value: presetPctLabel(settings.market, OUTPUT_COST_PRESET_PCT, t) },
    { key: "costs", label: t("forecastSettingsBar.inputCosts"), value: presetPctLabel(settings.costs, INPUT_COST_PRESET_PCT, t) },
  ];

  const reportDate = new Intl.DateTimeFormat(undefined, { dateStyle: "long" }).format(new Date());
  const subtitle = locationState?.fieldName || user?.farm?.name || "";

  const moneyUnit = t("scenarioComparison.perHectare", { value: getCurrencySymbol(currency) });

  const chartAndTableProps = {
    scenarios,
    projectionYears,
    forecastData,
    mainCropLabelLookup,
    soilSightOutcomes,
    marketPricePct: settings.market,
    costDevelopmentPct: settings.costs,
  };

  const chartSections: Array<{ viewMode: ViewMode; number: string; title: string; supporting: string; tableLabel: string }> = [
    {
      viewMode: "cumulative",
      number: "03",
      title: t("pdfReport.sectionCumulative"),
      supporting: t("pdfReport.sectionCumulativeSupporting"),
      tableLabel: t("pdfReport.tableMilestones", { unit: moneyUnit }),
    },
    {
      viewMode: "annual",
      number: "04",
      title: t("pdfReport.sectionAnnual"),
      supporting: t("pdfReport.sectionAnnualSupporting"),
      tableLabel: t("pdfReport.tableAnnual", { unit: moneyUnit }),
    },
    {
      viewMode: "yield",
      number: "05",
      title: t("pdfReport.sectionYield"),
      supporting: t("pdfReport.sectionYieldSupporting"),
      tableLabel: t("pdfReport.tableYield", { unit: t("outcomeComparison.unit.yield") }),
    },
  ];

  return (
    <MainPage fullWidth pageName="scenario-pdf-report" share={{ title: t("meta:soilSightReport.title") }}>
      {/* Hidden in @media print, so it never reaches the PDF. */}
      {!printReady && (
        <div className="scenario-print-overlay">
          <PageSpin />
          <span className="scenario-print-overlay-text">{t("nextSteps.downloadPrint.preparing")}</span>
        </div>
      )}
      <div className="pdf-report">
        {/* ── Page 1: cover and key insights ─────────────────────────────────────── */}
        <section className="pdf-section pdf-cover">
          <div className="pdf-cover-top">
            <Svg svgName="logos/klim_logo_text_black" className="pdf-cover-logo" />
            <div className="pdf-cover-date">
              <span className="pdf-cover-date-label">{t("pdfReport.generated")}</span>
              <span className="pdf-cover-date-value">{reportDate}</span>
            </div>
          </div>

          <div className="pdf-cover-hero">
            <Typography variant="h2" className="pdf-cover-title">
              {t("pdfReport.title")}
            </Typography>
            {subtitle && (
              <Typography variant="paragraph-m" className="pdf-cover-subtitle">
                {subtitle}
              </Typography>
            )}
          </div>

          <div className="pdf-cover-settings">
            {settingPills.map(({ key, label, value }) => (
              <span key={key} className="pdf-setting-pill">
                {label}
                <span className="pdf-setting-pill-value">{value}</span>
              </span>
            ))}
          </div>

          <div className="pdf-legend">
            <span className="pdf-legend-title">{t("pdfReport.legendTitle", { count: scenarios.length })}</span>
            {scenarios.map((scenario) => (
              <ScenarioLegendItem key={scenario.id} scenarios={scenarios} scenario={scenario} />
            ))}
          </div>

          {baseline && keyInsightRows.length > 0 && (
            <div className="pdf-key-insights">
              <SectionHeading title={t("scenarioComparison.title")} supporting={t("pdfReport.keyInsightsSupporting")} dataTestId="pdf-key-insights-heading" />
              {keyInsightRows.map(({ scenario, comparison }) => (
                <div key={scenario.id} className="pdf-key-insights-row" data-testid={`pdf-key-insights-${scenario.id}`}>
                  <span className="pdf-key-insights-scenario">
                    <ScenarioLegendItem scenarios={scenarios} scenario={scenario} />
                  </span>
                  <KeyInsightCards
                    comparison={comparison}
                    baseline={baseline}
                    subject={scenario}
                    soilSightOutcomes={soilSightOutcomes}
                    projectionYears={projectionYears}
                    currency={currency}
                    resilienceScores={resilienceScores}
                    onOpenChartView={noop}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Page 2: side by side and what the figures rest on ─────────────────── */}
        <section className="pdf-section pdf-section--break-before">
          {/* The download is blocked while figures are stale or calculating, so they are current here. */}
          <SideBySideSection
            scenarios={scenarios}
            soilSightOutcomes={soilSightOutcomes}
            projectionYears={projectionYears}
            currency={currency}
            resilienceScores={resilienceScores}
            isStale={false}
            isCalculating={false}
            onExplain={noop}
          />

          <div className="pdf-figures-basis">
            <Typography variant="h4" className="pdf-figures-basis-title">
              {t("pdfReport.figuresBasis")}
            </Typography>
            <EconomicsSection onEditEconomicInputs={noop} />
          </div>
        </section>

        {/* ── Pages 3–5: one chart per page, so more scenarios don't squash it ── */}
        {chartSections.map(({ viewMode, number, title, supporting, tableLabel }) => (
          <section key={viewMode} className="pdf-section pdf-section--break-before">
            <NumberedSectionHeader number={number} title={title} supporting={supporting} />
            <PdfChartAndTable viewMode={viewMode} testIdPrefix={viewMode} tableLabel={tableLabel} {...chartAndTableProps} />
          </section>
        ))}

        {/* ── Page 6: crop rotations ─────────────────────────────────────────────── */}
        <section className="pdf-section pdf-section--break-before">
          <NumberedSectionHeader number="06" title={t("pdfReport.sectionScenarios")} />
          <div className="pdf-rotations">
            {scenarios.map((scenario) => (
              <ScenarioPdfCard key={scenario.id} scenarios={scenarios} scenario={scenario} mainCropLabelLookup={mainCropLabelLookup} />
            ))}
          </div>
          <Typography variant="paragraph-s" className="pdf-disclaimer">
            {t("pdfReport.disclaimer")}
          </Typography>
        </section>
      </div>
    </MainPage>
  );
};

export default ScenarioPdfReport;
